// File: z__jsTimeline.js
// NOTE: This script defines the Alpine component for timeline sticky navigation

import Alpine from "alpinejs";
import Slider from "../utilities/Helpers/UI/Slider";
import * as Sentry from "@sentry/browser";

// Register via alpine:init to avoid race condition with Alpine.start() in main.js.
// The defer attribute does not guarantee this script executes before DOMContentLoaded
// in all edge cases (e.g. slow network, script load failure).
document.addEventListener("alpine:init", () => {
    Alpine.data("timeline", () => timelineComponent);
});

const timelineComponent = {
    showNav: false,
    timelineSection: null,
    navItems: [],
    sliderInstance: null,
    activeYearId: null,
    yearObservers: [],
    focusTimer: null,
    reachedEnd: false,
    endSentinel: null,
    endObserver: null,

    getNavItemsFromDataset() {
        const navData = this.$el?.dataset?.navItems;
        if (!navData) return [];

        try {
            const parsed = JSON.parse(navData);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            console.error("Failed to parse nav items:", e);
            console.error("Nav data:", navData);
            try {
                Sentry.captureException(e, {
                    tags: {
                        feature: "timeline",
                        component: "jsTimeline",
                        reason: "navItems JSON.parse",
                    },
                    extra: {
                        navData,
                    },
                });
            } catch (_) {
                // If Sentry isn't available for some reason, avoid breaking the component.
            }
            return [];
        }
    },

    getTimelineSectionFromDataset() {
        const sectionId = this.$el?.dataset?.timelineSectionId;
        if (!sectionId) return null;
        return document.getElementById(sectionId);
    },

    initializeEndSentinelObserver() {
        // Find end sentinel inside the section and arm reset when we reach it
        this.endSentinel = this.timelineSection?.querySelector?.("[data-timeline-end-sentinel]") || null;
        if (!this.endSentinel) return;

        this.endObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.reachedEnd = true;
                    }
                });
            },
            {
                threshold: 0,
                rootMargin: "0px 0px -20% 0px",
            }
        );

        this.endObserver.observe(this.endSentinel);
    },

    isTouchDesktop() {
        return window.matchMedia("(min-width: 1025px) and (pointer: coarse) and (hover: none)").matches;
    },

    getHeaderHeight() {
        const desktopHeader = document.getElementById("header");
        const mobileHeader = document.getElementById("mobile-header");
        let desktopHeight = 0;
        let mobileHeight = 0;

        if (desktopHeader) {
            const h = desktopHeader.getBoundingClientRect().height;
            if (Number.isFinite(h) && h > 0) desktopHeight = h;
        }

        if (mobileHeader) {
            const h = mobileHeader.getBoundingClientRect().height;
            if (Number.isFinite(h) && h > 0) mobileHeight = h;
        }

        // Pick the actually visible header height (desktop on non-touch lg+, mobile otherwise).
        if (desktopHeight > 0 || mobileHeight > 0) return Math.max(desktopHeight, mobileHeight);

        // If the header element isn't present (or has 0 height), don't guess.
        // Returning 0 avoids reintroducing hardcoded offsets into functional logic.
        return 0;
    },

    getTimelineNavHeight() {
        // NOTE: this.$el is the sticky timeline nav root (x-data="timeline")
        const navEl = this.$el;
        if (navEl) {
            const h = navEl.getBoundingClientRect().height;
            // When x-show hides the nav, height will be 0 (which is what we want).
            if (Number.isFinite(h) && h >= 0) return h;
        }

        // If the nav element isn't present, don't guess.
        return 0;
    },

    /**
     * @param {boolean} [assumeNavVisible] - when true (e.g. scrollToYear), use cached nav height if nav is currently hidden, so landing position is correct once nav appears
     */
    getScrollOffset(assumeNavVisible = false) {
        const header = this.getHeaderHeight();
        let navH = this.getTimelineNavHeight();
        if (navH <= 0 && assumeNavVisible) {
            const isDesktopNav = window.innerWidth >= 1025 && !this.isTouchDesktop();
            navH = this.cachedTimelineNavHeight || (isDesktopNav ? 77 : 48);
        }
        return header + navH + 50;
    },

    init() {
        // Parse nav items immediately so Alpine.js can render x-for
        this.navItems = this.getNavItemsFromDataset();

        // Use $nextTick to ensure DOM is ready
        this.$nextTick(() => {
            this.timelineSection = this.getTimelineSectionFromDataset();

            if (!this.timelineSection || this.navItems.length === 0) return;

            this.initializeEndSentinelObserver();

            // Initialize Intersection Observer for nav visibility
            this.initializeObserver();

            // Initialize Intersection Observers for each year to track active state
            this.initializeYearObservers();

            // Initialize slider for mobile - delay to ensure x-for has rendered all items
            setTimeout(() => {
                this.initializeSlider();
            }, 200);

            // Watch for activeYearId changes and scroll slider to active item on mobile
            this.$watch("activeYearId", (newYearId) => {
                if (newYearId && window.innerWidth < 1400) {
                    this.scrollSliderToActive();
                }
            });
        });
    },

    initializeObserver() {
        // Intersection Observer to show/hide nav based on timeline section visibility
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const rect = entry.boundingClientRect;
                    const viewportHeight = window.innerHeight;

                    // Show nav when timeline section is in viewport
                    // Hide when section is above (scrolled past) or below (not reached yet)
                    if (entry.isIntersecting) {
                        // Section is visible - show nav and cache its height for scroll-to-year
                        this.showNav = true;
                        this.cachedTimelineNavHeight = this.getTimelineNavHeight();
                    } else {
                        // Section is not intersecting
                        if (rect.bottom < 0) {
                            // Section is above viewport - we scrolled past it
                            this.showNav = false;
                        } else if (rect.top > viewportHeight) {
                            // Section is below viewport - we haven't reached it yet (user is above section)
                            this.showNav = false;

                            // If we previously reached the end of the section, reset animations for next visit
                            if (this.reachedEnd) {
                                window.dispatchEvent(new CustomEvent("timeline-reset"));
                                this.activeYearId = null;
                                this.reachedEnd = false;
                            }
                        }
                    }
                });
            },
            {
                threshold: 0,
                rootMargin: "-80px 0px 0px 0px",
            }
        );

        observer.observe(this.timelineSection);
    },

    initializeYearObservers() {
        // Track which year is most visible (closest to top of viewport)
        const updateActiveYear = () => {
            const headerOffset = this.getScrollOffset();
            const viewportTop = window.scrollY + headerOffset;
            let closestYear = null;
            let closestDistance = Infinity;

            this.navItems.forEach((item) => {
                const yearElement = document.getElementById(item.id);
                if (yearElement) {
                    const rect = yearElement.getBoundingClientRect();
                    const elementTop = window.scrollY + rect.top;
                    const distance = Math.abs(elementTop - viewportTop);

                    // Check if element is in viewport and closer than previous
                    if (rect.top < window.innerHeight && rect.bottom > headerOffset && distance < closestDistance) {
                        closestDistance = distance;
                        closestYear = item.id;
                    }
                }
            });

            if (closestYear) {
                this.activeYearId = closestYear;
            }
        };

        // Create Intersection Observer for each year section
        const yearObserver = new IntersectionObserver(
            (entries) => {
                // Update active year when any year enters/exits viewport
                updateActiveYear();
            },
            {
                threshold: [0, 0.1, 0.3, 0.5, 0.7, 1], // Multiple thresholds for better detection
                rootMargin: "-100px 0px -50% 0px", // Account for header
            }
        );

        // Observe all year sections
        this.navItems.forEach((item) => {
            const yearElement = document.getElementById(item.id);
            if (yearElement) {
                yearObserver.observe(yearElement);
                this.yearObservers.push({ observer: yearObserver, element: yearElement });
            }
        });

        // Initial update
        updateActiveYear();
    },

    scrollSliderToActive() {
        // Find index of active year in navItems
        const activeIndex = this.navItems.findIndex((item) => item.id === this.activeYearId);

        if (activeIndex !== -1 && this.sliderInstance && this.sliderInstance.slider) {
            // Scroll slider to active item
            this.sliderInstance.slider.slideTo(activeIndex, 300); // 300ms animation
        }
    },

    initializeSlider() {
        this.$nextTick(() => {
            const sliderEl = this.$refs?.timelineNavSlider;
            if (!sliderEl || this.navItems.length === 0) return;

            // Only initialize on mobile
            if (window.innerWidth >= 1400) return;

            // Wait until Alpine has rendered all slides
            const slides = sliderEl.querySelectorAll(".swiper-slide");
            if (slides.length !== this.navItems.length) {
                setTimeout(() => this.initializeSlider(), 100);
                return;
            }

            // If instance already exists (e.g., from previous init / re-render) – destroy it
            if (sliderEl.swiper) {
                sliderEl.swiper.destroy(true, true); // Also removes styles/classes and events
            }

            const customConfig = {
                direction: "horizontal",
                slidesPerView: 3,
                centeredSlides: true,
                spaceBetween: 0,
                watchOverflow: true,
                loop: false, // Important: keep false for proper navigation
                // Override default breakpoints to always show 3 slides on all screen sizes
                breakpoints: {
                    0: {
                        slidesPerView: 3,
                        spaceBetween: 0,
                        centeredSlides: true,
                    },
                    768: {
                        slidesPerView: 3, // Keep 3 slides even on tablets
                        spaceBetween: 0,
                        centeredSlides: true,
                    },
                    1400: {
                        slidesPerView: 3, // Keep 3 slides until desktop breakpoint
                        spaceBetween: 0,
                        centeredSlides: true,
                    },
                },
                navigation: {
                    nextEl: sliderEl.querySelector(".swiper-button-next"),
                    prevEl: sliderEl.querySelector(".swiper-button-prev"),
                },
            };

            this.sliderInstance = new Slider(sliderEl, customConfig);
        });
    },

    /**
     * Scroll to a year. Uses invisible scroll anchor (id="timeline-year-{block.id}") in liquid
     * so position is stable and not affected by entrance animations.
     */
    scrollToYear(yearId) {
        const anchor = document.getElementById(yearId);
        if (!anchor) return;

        this.activeYearId = yearId;
        const headerOffset = this.getScrollOffset(true);
        const top = anchor.getBoundingClientRect().top + window.pageYOffset - headerOffset;

        window.scrollTo({ top, behavior: "smooth" });

        // Move focus to the destination anchor for assistive tech users.
        anchor.setAttribute("tabindex", "-1");
        if (this.focusTimer) clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
            anchor.focus({ preventScroll: true });
        }, 350);
    },
};
